import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ensureValidToken } from "@/services/AuthAwareApiService";
import { TableProperties, Trash2, X } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface QualityScoreProps {
  score: number;
  qualityIssues?: string[];
  analysisId?: string;
  onAnalysisComplete?: () => void;
  hasSchema?: boolean;
  timeSeriesAnalysis?: {
    isTimeSeries: boolean;
    timeColumns: string[];
    valueColumns: string[];
    recommendation?: string;
  };
  hasQualityIssues?: boolean;
  hasTimeSeriesIssues?: boolean;
}

export const QualityScore = ({ 
  score, 
  qualityIssues = [], 
  analysisId,
  onAnalysisComplete,
  timeSeriesAnalysis,
  hasQualityIssues,
  hasTimeSeriesIssues,
}: QualityScoreProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localQualityIssues, setLocalQualityIssues] = useState(() => {
    // Add time series detection to quality issues if present
    const issues = [...qualityIssues];
    if (timeSeriesAnalysis?.isTimeSeries) {
      const timeColumns = timeSeriesAnalysis.timeColumns.join(', ');
      const valueColumns = timeSeriesAnalysis.valueColumns.join(', ');
      issues.push(
        `Time series data detected - Time columns: [${timeColumns}], Value columns: [${valueColumns}]. Consider restructuring for optimal analysis.`
      );
    }
    return issues;
  });

  const hasDuplicateRows = localQualityIssues.some(issue => 
    issue.toLowerCase().includes('duplicate') && issue.toLowerCase().includes('row')
  );

  const handleRestructureTimeSeries = async () => {
    if (!analysisId || !timeSeriesAnalysis) {
      toast({
        title: "Error",
        description: "Analysis ID and time series data are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(10);

      // Ensure valid token before making API call
      await ensureValidToken('restructure-time-series');

      const { error } = await supabase.functions.invoke('restructure-time-series', {
        body: { 
          analysisId,
          timeColumns: timeSeriesAnalysis.timeColumns,
          valueColumns: timeSeriesAnalysis.valueColumns
        }
      });

      if (error) throw error;

      setProgress(50);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProgress(80);

      if (onAnalysisComplete) {
        await onAnalysisComplete();
      }

      setProgress(100);

      toast({
        title: "Success",
        description: "Time series data restructured successfully.",
        className: "bg-[#F2FCE2] border-green-200 text-green-800",
      });

    } catch (error: any) {
      console.error('Error restructuring time series:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to restructure time series data",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setProgress(0);
      }, 1000);
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!analysisId) {
      toast({
        title: "Error",
        description: "Analysis ID is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(10);

      // Ensure valid token before making API call
      await ensureValidToken('remove-duplicates');

      const { error } = await supabase.functions.invoke('remove-duplicates', {
        body: { analysisId }
      });

      if (error) throw error;

      setProgress(50);

      if (onAnalysisComplete) {
        await onAnalysisComplete();
      }

      setProgress(100);

      toast({
        title: "Success",
        description: "Duplicates removed successfully.",
        className: "bg-[#F2FCE2] border-green-200 text-green-800",
      });

    } catch (error: any) {
      console.error('Error removing duplicates:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove duplicates",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setProgress(0);
      }, 1000);
    }
  };

  const handleRemoveIssue = async (issueIndex: number) => {
    const issue = localQualityIssues[issueIndex];
    if (!issue.toLowerCase().includes('column')) {
      return;
    }

    try {
      const columnMatch = issue.match(/Column[s]?\s+([^:]+)/);
      if (!columnMatch) return;

      const columnName = columnMatch[1].trim();

      if (!window.confirm(`Are you sure you want to remove the column "${columnName}" from the dataset? This action cannot be undone.`)) {
        return;
      }

      const newIssues = localQualityIssues.filter((_, index) => index !== issueIndex);
      setLocalQualityIssues(newIssues);

      // Ensure valid token before making API call
      await ensureValidToken('remove-column');

      const { error } = await supabase.functions.invoke('remove-column', {
        body: { 
          analysisId,
          columnName 
        }
      });

      if (error) throw error;

      toast({
        title: "Column Removed",
        description: `Column "${columnName}" has been removed from the dataset.`,
        className: "bg-[#F2FCE2] border-green-200 text-green-800",
      });

      if (onAnalysisComplete) {
        await onAnalysisComplete();
      }

    } catch (error: any) {
      console.error('Error removing column:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove column",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Top row with buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemoveDuplicates}
            disabled={isProcessing || !hasDuplicateRows}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove Duplicates
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestructureTimeSeries}
            disabled={isProcessing || !hasTimeSeriesIssues}
          >
            <TableProperties className="h-4 w-4 mr-2" />
            Restructure Time Series
          </Button>
        </div>
      </div>

      {/* Progress indicator */}
      {isProcessing && (
        <div className="space-y-2 bg-white rounded-lg border p-6">
          <Progress value={progress} className="w-full h-2" />
          <p className="text-sm text-muted-foreground text-center">
            Processing your request... {progress}%
          </p>
        </div>
      )}

      {/* Quality issues or success message */}
      {hasQualityIssues ? (
        <Alert variant="destructive" className="space-y-4">
          <div className="flex-1">
            <AlertTitle className="text-lg">Quality Issues Detected</AlertTitle>
            <AlertDescription>
              <ScrollArea className="h-[200px] w-full pr-4">
                <div className="space-y-2">
                  {localQualityIssues.map((issue, index) => {
                    const isColumnIssue = issue.toLowerCase().includes('column');
                    const isTimeSeriesIssue = issue.toLowerCase().includes('time series');
                    return (
                      <div key={index} className="flex items-start justify-between gap-2 text-sm group">
                        <span className={isTimeSeriesIssue ? 'text-orange-600 font-medium' : ''}>
                          {issue}
                        </span>
                        {isColumnIssue && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleRemoveIssue(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Remove this column from the dataset</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <Alert className={timeSeriesAnalysis?.isTimeSeries ? "bg-orange-50 border-orange-200" : "bg-green-50 border-green-200"}>
          <CheckCircle2 className={`h-5 w-5 ${timeSeriesAnalysis?.isTimeSeries ? "text-orange-600" : "text-green-600"}`} />
          <AlertTitle className={timeSeriesAnalysis?.isTimeSeries ? "text-orange-900" : "text-green-900"}>
            {timeSeriesAnalysis?.isTimeSeries ? "Data Quality Warning" : "Data Quality Check Passed"}
          </AlertTitle>
          <AlertDescription className={timeSeriesAnalysis?.isTimeSeries ? "text-orange-800" : "text-green-800"}>
            {timeSeriesAnalysis?.isTimeSeries ? (
              <>
                Time series data detected. While no critical quality issues were found, 
                you should consider restructuring the data for optimal analysis.
                Use the "Restructure Time Series" button above to optimize the data structure.
              </>
            ) : (
              "No significant quality issues were detected in your data."
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};